import { getApiBaseUrl, requestJson } from "@/lib/api";
import type { OrderStatus } from "@/lib/types";

export type PurchaseReportDateField = "DELIVERY_DATE" | "PURCHASE_DATE";

export interface PurchaseReportRequest {
  from?: string | null;
  to?: string | null;
  orderIds?: string[] | null;
  excludeStatuses?: OrderStatus[] | null;
  customerId?: string | null;
  groupByCustomer?: boolean;
  groupByOrder?: boolean;
  dateField?: PurchaseReportDateField;
}

export interface PurchaseReportLine {
  productId: string;
  productName: string;
  unit: string;
  liquidDemandQty: number;
  totalDemandQty: number;
  handledQty: number;
  pendingDemandQty: number;
  alreadyPurchasedQty: number;
  fromStockQty: number;
  remainingToPurchaseQty: number;
  contributingOrderIds: string[];
}

export interface PurchaseReportOrderRef {
  id: string;
  shortId: string;
  clientName: string;
  deliveryDate?: string | null;
  status: OrderStatus;
}

export interface PurchaseReportTotals {
  productCount: number;
  totalDemandQty: number;
  handledQty: number;
  alreadyPurchasedQty: number;
  fromStockQty: number;
  remainingToPurchaseQty: number;
}

export interface PurchaseReportCustomerBreakdown {
  customerId: string;
  customerName: string | null;
  orderCount: number;
  lines: PurchaseReportLine[];
}

export interface PurchaseReportOrderBreakdown {
  orderId: string;
  shortId: string;
  clientName: string | null;
  deliveryDate?: string | null;
  status: OrderStatus;
  lines: PurchaseReportLine[];
}

export interface PurchaseReport {
  from?: string | null;
  to?: string | null;
  orderCount: number;
  orders: PurchaseReportOrderRef[];
  lines: PurchaseReportLine[];
  totals: PurchaseReportTotals;
  byCustomer?: PurchaseReportCustomerBreakdown[] | null;
  byOrder?: PurchaseReportOrderBreakdown[] | null;
}

export async function fetchPurchaseReport(request: PurchaseReportRequest): Promise<PurchaseReport> {
  return requestJson<PurchaseReport>("/api/v1/reports/purchase-list", {
    method: "POST",
    body: request,
  });
}

export async function downloadPurchaseReportExport(
  request: PurchaseReportRequest,
  format: "pdf" | "xlsx",
): Promise<{ filename: string; blob: Blob }> {
  const response = await fetch(`${getApiBaseUrl()}/api/v1/reports/purchase-list/export.${format}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    throw new Error("Não foi possível gerar o arquivo");
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? `lista-compras.${format}`;
  const blob = await response.blob();
  return { filename, blob };
}
